/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { getDayOfWeek, CalendarDateTime, toZoned } from '@internationalized/date';
import {
    parse,
    parseISO,
    isValid,
    format,
    isAfter,
    isBefore,
    isEqual,
    startOfDay,
} from 'date-fns';
import { DateValue } from 'react-aria';
import * as types from '../constants';

type DateRangeUnit = 'month' | 'year' | 'week' | 'day' | 'hour' | 'minute' | 'second';
type InclusivityType = '()' | '[]' | '[)' | '(]';

export const fromCalendarDate = (
    date: CalendarDateTime | null,
    formatPattern = 'MM/dd/yyyy',
    tz?: string
): string | null => {
    if (!date) return null;

    try {
        const dateObject = tz
            ? date.toDate(tz)
            : new Date(
                  date.year,
                  date.month - 1,
                  date.day,
                  date.hour ?? 0,
                  date.minute ?? 0,
                  date.second ?? 0,
                  date.millisecond ?? 0
              );

        return format(dateObject, formatPattern);
    } catch {
        return null;
    }
};

class DateOperationsClass extends CalendarDateTime {
    constructor(private date: CalendarDateTime) {
        super(
            date.year,
            date.month,
            date.day,
            date.hour,
            date.minute,
            date.second,
            date.millisecond
        );
    }

    private compareDay(other: CalendarDateTime): -1 | 0 | 1 {
        if (this.year !== other.year) {
            return this.year < other.year ? -1 : 1;
        }
        if (this.month !== other.month) {
            return this.month < other.month ? -1 : 1;
        }
        if (this.day !== other.day) {
            return this.day < other.day ? -1 : 1;
        }
        return 0;
    }

    tz(timezone?: string): DateOperationsClass | null {
        const tz = timezone;

        if (!this || !tz) return null;

        try {
            Intl.DateTimeFormat(undefined, { timeZone: tz });
        } catch {
            throw new Error(`Invalid timezone: ${tz}`);
        }

        // Convert the date to the target timezone
        const convertedDateTime = toZoned(this, tz);

        // Return new instance with converted date
        return new DateOperationsClass(
            new CalendarDateTime(
                convertedDateTime.year,
                convertedDateTime.month,
                convertedDateTime.day,
                convertedDateTime.hour,
                convertedDateTime.minute,
                convertedDateTime.second,
                convertedDateTime.millisecond
            )
        );
    }

    startOf(unit: DateRangeUnit): DateOperationsClass {
        switch (unit) {
            case 'month':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        1, // First day of month
                        0, // Reset hour
                        0, // Reset minute
                        0, // Reset second
                        0 // Reset millisecond
                    )
                );
            case 'year':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        1, // First month
                        1, // First day
                        0, // Reset hour
                        0, // Reset minute
                        0, // Reset second
                        0 // Reset millisecond
                    )
                );
            case 'week': {
                const newDate = new CalendarDateTime(
                    this.year,
                    this.month,
                    this.day,
                    0, // Reset hour
                    0, // Reset minute
                    0, // Reset second
                    0 // Reset millisecond
                );
                const dayOfWeek = getDayOfWeek(newDate, 'en-US');
                return new DateOperationsClass(super.add({ days: -dayOfWeek }));
            }
            case 'day':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        0, // Reset hour
                        0, // Reset minute
                        0, // Reset second
                        0 // Reset millisecond
                    )
                );
            case 'hour':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        this.hour,
                        0, // Reset minute
                        0, // Reset second
                        0 // Reset millisecond
                    )
                );
            case 'minute':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        this.hour,
                        this.minute,
                        0, // Reset second
                        0 // Reset millisecond
                    )
                );
            case 'second':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        this.hour,
                        this.minute,
                        this.second,
                        0 // Reset millisecond
                    )
                );
            default:
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        this.hour,
                        this.minute,
                        this.second,
                        this.millisecond
                    )
                );
        }
    }

    endOf(unit: DateRangeUnit): DateOperationsClass {
        switch (unit) {
            case 'month':
                return new DateOperationsClass(
                    new CalendarDateTime(this.year, this.month, 1)
                        .add({ months: 1 })
                        .add({
                            days: -1,
                            hours: 23,
                            minutes: 59,
                            seconds: 59,
                            milliseconds: 999,
                        })
                );
            case 'year':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        12, // Last month
                        31, // Last day
                        23, // Last hour
                        59, // Last minute
                        59, // Last second
                        999 // Last millisecond
                    )
                );
            case 'week':
                return this.startOf('week').add({
                    days: 6,
                    hours: 23,
                    minutes: 59,
                    seconds: 59,
                    milliseconds: 999,
                });
            case 'day':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        23, // Last hour
                        59, // Last minute
                        59, // Last second
                        999 // Last millisecond
                    )
                );
            case 'hour':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        this.hour,
                        59, // Last minute
                        59, // Last second
                        999 // Last millisecond
                    )
                );
            case 'minute':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        this.hour,
                        this.minute,
                        59, // Last second
                        999 // Last millisecond
                    )
                );
            case 'second':
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        this.hour,
                        this.minute,
                        this.second,
                        999 // Last millisecond
                    )
                );
            default:
                return new DateOperationsClass(
                    new CalendarDateTime(
                        this.year,
                        this.month,
                        this.day,
                        this.hour,
                        this.minute,
                        this.second,
                        this.millisecond
                    )
                );
        }
    }

    format(formatValue: Parameters<typeof fromCalendarDate>[1] = 'MM/DD/YYYY'): string {
        const result = fromCalendarDate(this, formatValue);
        if (!result) {
            throw new Error('Error formatting date');
        }
        return result;
    }

    add(duration: {
        years?: number;
        months?: number;
        days?: number;
        hours?: number;
        minutes?: number;
        seconds?: number;
        milliseconds?: number;
    }): DateOperationsClass {
        const result = super.add(duration);
        return new DateOperationsClass(result);
    }

    subtract(duration: {
        years?: number;
        months?: number;
        days?: number;
        hours?: number;
        minutes?: number;
        seconds?: number;
        milliseconds?: number;
    }): DateOperationsClass {
        const result = super.subtract(duration);
        return new DateOperationsClass(result);
    }

    utc(): DateOperationsClass {
        const utcDate = this.toDate('UTC');
        return new DateOperationsClass(
            new CalendarDateTime(
                utcDate.getUTCFullYear(),
                utcDate.getUTCMonth() + 1,
                utcDate.getUTCDate(),
                utcDate.getUTCHours(),
                utcDate.getUTCMinutes(),
                utcDate.getUTCSeconds(),
                utcDate.getUTCMilliseconds()
            )
        );
    }

    isBefore(other: CalendarDateTime): boolean {
        return this.compare(other) < 0;
    }

    isAfter(other: CalendarDateTime): boolean {
        return this.compare(other) > 0;
    }

    isSame(other: CalendarDateTime): boolean {
        return this.compare(other) === 0;
    }

    isSameOrAfter(other: CalendarDateTime): boolean {
        return this.compare(other) >= 0;
    }

    isBetween(
        start: CalendarDateTime,
        end: CalendarDateTime,
        inclusivity: InclusivityType = '[]',
        compareTime = false
    ): boolean {
        // Handle invalid ranges
        if (start.compare(end) > 0) {
            throw new Error('Start date must be before or equal to end date');
        }

        const comparison = compareTime
            ? this.compare.bind(this)
            : this.compareDay.bind(this);

        const startComparison = comparison(start);
        const endComparison = comparison(end);

        switch (inclusivity) {
            case '()':
                return startComparison > 0 && endComparison < 0;
            case '[]':
                return startComparison >= 0 && endComparison <= 0;
            case '[)':
                return startComparison >= 0 && endComparison < 0;
            case '(]':
                return startComparison > 0 && endComparison <= 0;
            default:
                throw new Error('Invalid inclusivity parameter');
        }
    }

    diff(
        date: CalendarDateTime | Date | string,
        unit: 'hours' | 'days' | 'months' | 'years' = 'days'
    ): number {
        const compareDate =
            date instanceof CalendarDateTime ? date : toCalendarDate(date);

        const startDate = new Date(this.year, this.month - 1, this.day);
        const endDate = new Date(
            compareDate.year,
            compareDate.month - 1,
            compareDate.day
        );

        switch (unit) {
            case 'hours': {
                const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                return Math.floor(diffTime / (1000 * 60 * 60));
            }
            case 'days': {
                const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }

            case 'months': {
                const yearDiff = endDate.getFullYear() - startDate.getFullYear();
                const monthDiff = endDate.getMonth() - startDate.getMonth();
                return Math.abs(yearDiff * 12 + monthDiff);
            }

            case 'years': {
                return Math.abs(endDate.getFullYear() - startDate.getFullYear());
            }

            default:
                throw new Error(`Unsupported unit: ${unit}`);
        }
    }

    static isValid(date: DateValue | string | Date | CalendarDateTime | null): boolean {
        if (!date) return false;

        if (date instanceof CalendarDateTime) {
            return true;
        }

        if (
            typeof date === 'object' &&
            'year' in date &&
            'month' in date &&
            'day' in date
        ) {
            try {
                // Validate year
                if (!Number.isInteger(date.year)) return false;

                // Validate month (1-12)
                if (!Number.isInteger(date.month) || date.month < 1 || date.month > 12) {
                    return false;
                }

                // Validate day based on month and year
                const daysInMonth = new Date(date.year, date.month, 0).getDate();
                if (
                    !Number.isInteger(date.day) ||
                    date.day < 1 ||
                    date.day > daysInMonth
                ) {
                    return false;
                }

                return true;
            } catch {
                return false;
            }
        }

        // Handle Date object
        if (date instanceof Date) {
            return !Number.isNaN(date.getTime());
        }

        // Handle string
        try {
            const parsed = new Date(date);
            return !Number.isNaN(parsed.getTime());
        } catch {
            return false;
        }
    }

    isValid(): boolean {
        return DateOperationsClass.isValid(this);
    }
}

export const parseTimeString = (timeStr: string): Date => {
    // Handle 'h:m a' format (12-hour with AM/PM)
    const twelveHourRegex = /^(\d{1,2}):(\d{1,2})\s*(am|pm|AM|PM)$/;
    const twelveHourMatch = timeStr.match(twelveHourRegex);

    if (twelveHourMatch) {
        const [, hours, minutes, period] = twelveHourMatch;
        let hour = parseInt(hours, 10);
        const minute = parseInt(minutes, 10);

        // Validate minutes
        if (minute < 0 || minute > 59) {
            throw new Error('Invalid minutes');
        }

        // Validate hours
        if (hour < 1 || hour > 12) {
            throw new Error('Invalid hours for 12-hour format');
        }

        // Convert to 24-hour format
        if (period.toLowerCase() === 'pm' && hour !== 12) {
            hour += 12;
        } else if (period.toLowerCase() === 'am' && hour === 12) {
            hour = 0;
        }

        const today = new Date();
        today.setHours(hour, minute, 0, 0);
        return today;
    }

    const twentyFourHourRegex = /^(\d{1,2}):(\d{1,2})$/;
    const twentyFourHourMatch = timeStr.match(twentyFourHourRegex);

    if (twentyFourHourMatch) {
        const [, hours, minutes] = twentyFourHourMatch;
        const hour = parseInt(hours, 10);
        const minute = parseInt(minutes, 10);

        // Validate hours and minutes
        if (hour < 0 || hour > 23) {
            throw new Error('Invalid hours for 24-hour format');
        }
        if (minute < 0 || minute > 59) {
            throw new Error('Invalid minutes');
        }

        const today = new Date();
        today.setHours(hour, minute, 0, 0);
        return today;
    }

    throw new Error('Invalid time format');
};

export const toCalendarDate = (
    dateInput: string | Date | CalendarDateTime | DateValue | null
): CalendarDateTime => {
    if (!dateInput) {
        const now = new Date();
        return new CalendarDateTime(
            now.getFullYear(),
            now.getMonth() + 1,
            now.getDate(),
            now.getHours(),
            now.getMinutes()
        );
    }

    try {
        if (dateInput instanceof CalendarDateTime) {
            return dateInput;
        }

        if (dateInput instanceof Date) {
            return new CalendarDateTime(
                dateInput.getFullYear(),
                dateInput.getMonth() + 1,
                dateInput.getDate(),
                dateInput.getHours(),
                dateInput.getMinutes()
            );
        }

        if (
            typeof dateInput === 'object' &&
            'year' in dateInput &&
            'month' in dateInput &&
            'day' in dateInput
        ) {
            return new CalendarDateTime(
                dateInput.year,
                dateInput.month,
                dateInput.day,
                // @ts-expect-error
                dateInput.hours || 0,
                // @ts-expect-error
                dateInput.minutes || 0
            );
        }

        const trimmedInput =
            (typeof dateInput === 'string' ? dateInput.trim() : '') || '';

        // Matches full ISO 8601 format (including time and timezone)
        const isoRegex =
            /^(\d{4}-\d{2}-\d{2})(T\d{2}:\d{2}:\d{2}(?:\.\d{3})?)(Z|([+-]\d{2}:\d{2}))?$/;
        if (isoRegex.test(trimmedInput)) {
            const parsedDate = parseISO(trimmedInput);
            if (isValid(parsedDate)) {
                return new CalendarDateTime(
                    parsedDate.getFullYear(),
                    parsedDate.getMonth() + 1,
                    parsedDate.getDate(),
                    parsedDate.getHours(),
                    parsedDate.getMinutes()
                );
            }
        }

        // ISO 8601 format
        if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?(Z)?$/.test(trimmedInput)) {
            const parsedDate = parseISO(trimmedInput);
            if (isValid(parsedDate)) {
                return new CalendarDateTime(
                    parsedDate.getFullYear(),
                    parsedDate.getMonth() + 1,
                    parsedDate.getDate(),
                    parsedDate.getHours(),
                    parsedDate.getMinutes()
                );
            }
        }

        // MM/YYYY
        if (/^\d{2}\/\d{4}$/.test(trimmedInput)) {
            const [month, year] = trimmedInput.split('/').map(Number);
            return new CalendarDateTime(year, month, 1);
        }

        // MM/DD/YYYY or M/D/YYYY
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmedInput)) {
            const [month, day, year] = trimmedInput.split('/').map(Number);
            return new CalendarDateTime(year, month, day);
        }

        // MM-DD-YYYY or M-D-YYYY
        if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmedInput)) {
            const [month, day, year] = trimmedInput.split('-').map(Number);
            return new CalendarDateTime(year, month, day);
        }

        // HH:mm (24-hour time-only format)
        if (/^\d{1,2}:\d{2}$/.test(trimmedInput)) {
            const [hours, minutes] = trimmedInput.split(':').map(Number);
            const now = new Date();
            return new CalendarDateTime(
                now.getFullYear(),
                now.getMonth() + 1,
                now.getDate(),
                hours,
                minutes
            );
        }

        // h:mm AM/PM (12-hour time-only format)
        if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(trimmedInput)) {
            const [time, meridiem] = trimmedInput.split(/\s+/);
            // eslint-disable-next-line  prefer-const
            let [hours, minutes] = time.split(':').map(Number);
            if (meridiem.toLowerCase() === 'pm' && hours !== 12) {
                hours += 12;
            } else if (meridiem.toLowerCase() === 'am' && hours === 12) {
                hours = 0;
            }
            const now = new Date();
            return new CalendarDateTime(
                now.getFullYear(),
                now.getMonth() + 1,
                now.getDate(),
                hours,
                minutes
            );
        }

        // MM/DD/YYYY HH:mm or MM-DD-YYYY HH:mm
        if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}\s+\d{1,2}:\d{2}$/.test(trimmedInput)) {
            const [datePart, timePart] = trimmedInput.split(/\s+/);
            const [month, day, year] = datePart.split(/[-/]/).map(Number);
            const [hours, minutes] = timePart.split(':').map(Number);
            return new CalendarDateTime(year, month, day, hours, minutes);
        }

        // Try fallback with multiple formats
        const commonFormats = [
            'yyyy-MM-dd',
            'yyyy-MM-dd HH:mm',
            'MM/dd/yyyy',
            'MM/dd/yyyy HH:mm',
        ];
        for (const formatValue of commonFormats) {
            const parsedDate = parse(trimmedInput, formatValue, new Date());
            if (isValid(parsedDate)) {
                return new CalendarDateTime(
                    parsedDate.getFullYear(),
                    parsedDate.getMonth() + 1,
                    parsedDate.getDate(),
                    parsedDate.getHours(),
                    parsedDate.getMinutes()
                );
            }
        }

        throw new Error('Unsupported date format');
    } catch {
        const now = new Date();
        return new CalendarDateTime(
            now.getFullYear(),
            now.getMonth() + 1,
            now.getDate(),
            now.getHours(),
            now.getMinutes()
        );
    }
};

export const dateHelper = (
    dateInput?: string | Date | CalendarDateTime | null | DateValue
): DateOperationsClass => {
    const date = dateInput ?? new Date();
    const dateValue = toCalendarDate(date);
    return new DateOperationsClass(dateValue);
};

export const isBetweenDays = (
    date: any,
    startDate: any,
    endDate: any,
    inclusive = false
) => {
    const day = startOfDay(date);
    const start = startOfDay(startDate);
    const end = startOfDay(endDate);

    if (inclusive) {
        return (
            (isAfter(day, start) || isEqual(day, start)) &&
            (isBefore(day, end) || isEqual(day, end))
        );
    }
    return isAfter(day, start) && isBefore(day, end);
};

export const formatTripPlannerDate = (date: string) => {
    try {
        if (!date) return '';
        return format(new Date(startOfDay(date).toISOString()), types.FNS_DATE_FORMAT);
    } catch {
        return '';
    }
};
